import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import type { ApiError, Organization, ProjectSummary, User } from '@nx/state';
import {
  MFA_STATUS_REASON,
  consoleApi,
  getAccessToken,
  getApiError,
  getErrorMessage,
  isErrorWithMessage,
  isFetchBaseQueryError,
  login,
  useAuth,
  useInternalConfiguration,
  useNotifications,
  useOnboardingConfiguration,
  useToolsConfiguration,
  useUnsafeAppContext,
} from '@nx/state';
import { isNonEmptyString, isNotNullish, isNullish } from '@nx/stdlib';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { JwtPayload } from 'jwt-decode';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';

import { useIsOrgContext } from './hooks';

const logger = createLogger(APP_SCOPE.aura);

export const getAuthenticatedSsoOrgId = async () => {
  const accessToken = await getAccessToken();
  if (isNotNullish(accessToken)) {
    const token = jwtDecode<JwtPayload & { org_id?: string }>(accessToken);
    return token.org_id;
  }
  return undefined;
};

export const maybeAuthenticateWithSsoOrg = async (ssoOrgId: string) => {
  try {
    const tokenOrgId = await getAuthenticatedSsoOrgId();
    if (isNullish(tokenOrgId) || tokenOrgId !== ssoOrgId) {
      await login(
        ssoOrgId,
        {
          extraQueryParams: { organization: ssoOrgId, screen_hint: 'signin' },
          state: { returnTo: location.pathname + location.search },
        },
        true,
      );
    }
  } catch (error) {
    if (error instanceof Error) {
      // Intercept the error and log it for app monitoring
      logger.info('Attempt to authenticate with organization SSO resulted in error', error.message);
    }
  }
};

// This function will take care of the expected routing and only reroute the user if the right conditions are meet.
// If you navigate to route we will always re-evaluate the app-context. The same goes if the user is in project/org
// context and the url has changed. If nothing has changed we will not re-evaluate the app-context.
const shouldSkip = (
  path: string,
  isOrgContext: boolean,
  activeOrgId: string | null,
  urlOrgId: string | null | undefined,
  activeProjectId: string | null,
  urlProjectId: string | null | undefined,
) => {
  if (path === '/') {
    return false;
  }

  if (isOrgContext) {
    if (!isNonEmptyString(activeOrgId)) {
      return false;
    }

    if (isNonEmptyString(urlOrgId) && urlOrgId !== activeOrgId) {
      return false;
    }

    return true;
  }
  if (!isNonEmptyString(activeProjectId)) {
    return false;
  }

  if (isNonEmptyString(urlProjectId) && urlProjectId !== activeProjectId) {
    return false;
  }

  return true;
};

const parseRouteParams = (route: string) => {
  const orgId = matchPath('/org/:orgId/*', route)?.params.orgId;
  const projectId = matchPath('/projects/:projectId/*', route)?.params.projectId;
  return { orgId, projectId };
};

enum ACTION {
  Redirect,
  SetOrg,
  SetProject,
  Onboarding,
}

type ResolvedAppContext =
  | { action: ACTION.Redirect; route: string }
  | { action: ACTION.SetOrg; orgId: string }
  | { action: ACTION.SetProject; orgId: string; projectId: string }
  | { action: ACTION.Onboarding; orgId: string; projectId?: string };

/**
 * Gets the default project based on the following order:
 * 1. Non-suspended project of SSO org that user is logged in to
 * 2. Project of SSO org that user is logged in to even if suspended
 * 3. Stored project that doesn't require SSO login and is not suspended
 * 4. Default project if it does not require SSO login and is not suspended
 * 5. Any non-suspended project of org that does not require SSO login
 * 6. User default project if it is not suspended
 * 7. Any non-suspended project
 * 8. Last viewed even if suspended
 * 9. User default project even if suspended
 * @param projects
 * @param user
 * @returns The default project or null if none found
 */
const getDefaultProject = (
  projects: ProjectSummary[],
  user: User,
  ssoOrgId: string | undefined,
): ProjectSummary | null => {
  const storedProjectId = localStorage.getItem(`default-project-${user.id}`);
  if (isNotNullish(ssoOrgId)) {
    return (
      projects.find((p) => p.ssoOrganizationId === ssoOrgId && !p.suspended && p.id === storedProjectId) ??
      projects.find((p) => p.ssoOrganizationId === ssoOrgId && !p.suspended) ??
      projects.find((p) => p.ssoOrganizationId === ssoOrgId && p.id === storedProjectId) ??
      projects.find((p) => p.ssoOrganizationId === ssoOrgId) ??
      projects.find(Boolean) ??
      null
    );
  }

  return (
    projects.find((p) => isNullish(p.ssoOrganizationId) && p.id === storedProjectId && !p.suspended) ??
    projects.find((p) => isNullish(p.ssoOrganizationId) && p.id === user.defaultProject && !p.suspended) ??
    projects.find((p) => isNullish(p.ssoOrganizationId) && !p.suspended) ??
    projects.find((p) => p.id === user.defaultProject && !p.suspended) ??
    projects.find((p) => !p.suspended) ??
    projects.find((p) => p.id === storedProjectId) ??
    projects.find((p) => p.id === user.defaultProject) ??
    projects.find(Boolean) ??
    null
  );
};

const resolveAppContext = (
  orgs: Organization[],
  projects: ProjectSummary[],
  user: User,
  ssoOrgId: string | undefined,
  route: string,
  isAppRoute: boolean,
): ResolvedAppContext => {
  const removePlanTypeEnabled = user.featureToggles['console-remove-plantype'];
  const planTypeProjects = removePlanTypeEnabled ? projects : projects.filter((p) => p.planType === user.planType);
  const planTypeOrgs = removePlanTypeEnabled ? orgs : orgs.filter((o) => o.planType === user.planType);
  const { orgId, projectId } = parseRouteParams(route);

  // If orgId param is valid set active org
  const org = planTypeOrgs.find((o) => o.id === orgId);
  // If projectId param is in url set that as active project/org
  const project = planTypeProjects.find((p) => p.id === projectId) ?? null;

  // If no project in url, select active based on user and org
  const defaultProject = getDefaultProject(planTypeProjects, user, ssoOrgId);

  if (isNonEmptyString(orgId) && isNotNullish(org)) {
    if (!org.onboardingCompleted) {
      return { action: ACTION.Onboarding, orgId };
    }
    return { action: ACTION.SetOrg, orgId };
  }

  if (isNonEmptyString(projectId) && isNotNullish(project)) {
    const projectOrg = orgs.find((o) => o.id === project.organizationId);
    if (isNotNullish(projectOrg) && !projectOrg.onboardingCompleted) {
      return { action: ACTION.Onboarding, orgId: projectOrg.id, projectId: project.id };
    }
    return {
      action: ACTION.SetProject,
      orgId: project.organizationId,
      projectId: project.id,
    };
  }

  if (!defaultProject) {
    // If no project, find first org and redirect to the org context
    const firstOrg = orgs.find(Boolean);
    if (firstOrg) {
      if (!firstOrg.onboardingCompleted) {
        return { action: ACTION.Onboarding, orgId: firstOrg.id };
      }
      return { action: ACTION.Redirect, route: `/org/${firstOrg.id}/projects` };
    }
    throw new Error('No org or project found');
  }
  const projectOrg = orgs.find((o) => o.id === defaultProject.organizationId);

  if (isNotNullish(projectOrg) && !projectOrg.onboardingCompleted) {
    return { action: ACTION.Onboarding, orgId: projectOrg.id, projectId: defaultProject.id };
  }

  // If the current route is a tool route, set active project
  if (isAppRoute) {
    return {
      action: ACTION.SetProject,
      orgId: defaultProject.organizationId,
      projectId: defaultProject.id,
    };
  }
  const defaultOrg = planTypeOrgs.find((o) => o.id === defaultProject.id);
  const homePagePath = defaultOrg?.featureToggles['plg-home-page-types'] ?? 'none';
  // Redirect to the project context
  return {
    action: ACTION.Redirect,
    route: `/projects/${defaultProject.id}/${homePagePath !== 'none' ? 'home' : 'instances'}`,
  };
};

export const useAppContextInit = () => {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isOrgContext = useIsOrgContext();
  const internalConfiguration = useInternalConfiguration();
  const toolsConfiguration = useToolsConfiguration();
  const onboardingConfiguration = useOnboardingConfiguration();
  const { addNotification } = useNotifications();

  const [listOrgs] = consoleApi.useLazyListOrganizationsByUserQuery();
  const [listProjects] = consoleApi.useLazyListProjectsByUserQuery();

  const { activeOrgId, activeProjectId, updateActiveOrg, updateActiveProject, updateUserId } = useUnsafeAppContext();
  const [error, setError] = useState<ApiError | null>(null);
  const { data: user } = consoleApi.useGetUserDetailsQuery();
  const [maybeUpdateMfaEnrollmentStatus] = consoleApi.useMaybeUpdateMfaEnrollmentStatusMutation();
  const { projectId: urlProjectId, orgId: urlOrgId } = parseRouteParams(location.pathname);

  useEffect(() => {
    void (async function init() {
      try {
        setError(null);

        if (
          isNullish(auth.user) ||
          isNullish(user) ||
          shouldSkip(location.pathname, isOrgContext, activeOrgId, urlOrgId, activeProjectId, urlProjectId)
        ) {
          return;
        }

        if (isNotNullish(user) && user.mfaStatus.enabled && user.mfaStatus.reason === MFA_STATUS_REASON.NOT_ENROLLED) {
          maybeUpdateMfaEnrollmentStatus({
            userId: user.id,
          })
            .unwrap()
            .catch((e: FetchBaseQueryError | SerializedError | undefined) => {
              const err = getApiError(e);
              if (isNotNullish(err.message)) {
                logger.error(err.message);
              }
              addNotification({
                type: 'danger',
                title: 'Failed to update MFA enrollment status.',
                description: getErrorMessage(err),
                timeout: 5000,
              });
            });
        }
        const [orgs, projects] = await Promise.all([listOrgs(user.id).unwrap(), listProjects(user.id).unwrap()]);

        const guidesPath = toolsConfiguration.guides?.route;
        const onboardingPath = onboardingConfiguration?.route;

        const authenticatedSsoOrgId = await getAuthenticatedSsoOrgId();
        const appContext = resolveAppContext(
          orgs,
          projects,
          user,
          authenticatedSsoOrgId,
          location.pathname,
          location.pathname.startsWith(internalConfiguration.basePath) ||
            (guidesPath !== undefined && location.pathname.startsWith(guidesPath)) ||
            (onboardingPath !== undefined && location.pathname.startsWith(onboardingPath)) ||
            location.pathname === '/invite' ||
            location.pathname.startsWith('/account') ||
            location.pathname.startsWith('/orgs'),
        );
        // TODO: Find a better way to handle new paths redirecting to project home when reloading page (https://trello.com/c/KujshTdv/8191-prevent-new-paths-from-redirecting-to-project-instances-on-reload)

        updateUserId(user.id);

        if (appContext.action === ACTION.Redirect) {
          void navigate(appContext.route, { replace: true });
        } else if (appContext.action === ACTION.SetOrg) {
          updateActiveOrg(appContext.orgId);
        } else if (appContext.action === ACTION.Onboarding) {
          if (isNotNullish(appContext.projectId)) {
            localStorage.setItem(`default-project-${user.id}`, appContext.projectId);
            updateActiveProject({
              orgId: appContext.orgId,
              projectId: appContext.projectId,
            });
          } else {
            updateActiveOrg(appContext.orgId);
          }
          if (isNotNullish(onboardingConfiguration)) {
            void navigate(onboardingConfiguration.route, { replace: true });
          }
        } else {
          localStorage.setItem(`default-project-${user.id}`, appContext.projectId);
          updateActiveProject({
            orgId: appContext.orgId,
            projectId: appContext.projectId,
          });
        }
      } catch (err) {
        const isFetchError = isFetchBaseQueryError(err);
        let consoleError: ApiError | null = null;
        if (isFetchError) {
          consoleError = getApiError(err);
        }
        const msg =
          (isNotNullish(consoleError) && getErrorMessage(consoleError)) ||
          (isErrorWithMessage(err) && err.message) ||
          `Unknown error: ${JSON.stringify(err)}`;
        const apiError: ApiError = {
          type: 'UNKNOWN_ERROR',
          reason: undefined,
          code: undefined,
          extra: undefined,
          validationErrors: undefined,
          ...consoleError,
          message: msg,
        };
        setError(apiError);
      }
    })();
  }, [
    activeOrgId,
    activeProjectId,
    auth.user,
    internalConfiguration.basePath,
    isOrgContext,
    listOrgs,
    listProjects,
    location.pathname,
    navigate,
    toolsConfiguration.guides?.route,
    onboardingConfiguration,
    updateActiveOrg,
    updateActiveProject,
    updateUserId,
    maybeUpdateMfaEnrollmentStatus,
    user,
    urlOrgId,
    urlProjectId,
    addNotification,
  ]);

  // We consider that we are in a loading state if we either don't have an active org or if the path has changed and we
  // otherwise would have not re-evaluated the app-context,
  const isLoading =
    isNullish(activeOrgId) ||
    !shouldSkip(location.pathname, isOrgContext, activeOrgId, urlOrgId, activeProjectId, urlProjectId);

  return {
    isLoading,
    error,
  };
};
